import {red, teal, slate, yellow, stone} from "tailwindcss/colors"
import {useLayoutStore} from "@/store/layout"

export const $getCurrentModule = () => {
    const module = useLayoutStore().currentModule
    return module ? appModules[module] : null
}

export const $getModuleIcon = (module?: string): string | null => {
    if (!module) return $getCurrentModule()?.icon ?? null
    return appModules[module]?.icon ?? null
}

export const $getModuleColor = (module?: string): string | null => {
    if (!module) return $getCurrentModule()?.color ?? null
    return appModules[module]?.color ?? null
}

export const appColors = {
    red,
    teal,
    slate,
    yellow,
    stone,
    primary: {
        "50": "#fafbea",
        "100": "#f2f6d1",
        "200": "#e6eea8",
        "300": "#d1e076",
        "400": "#bcd04b",
        "500": "#b1cb32",
        "600": "#7b9020",
        "700": "#5d6e1d",
        "800": "#4c581c",
        "900": "#404b1c",
        "950": "#21290a",
        DEFAULT: "#b1cb32",
    }
}

export const appModules: AppModules = {
    primary: {
        name: 'primary',
        color: appColors.primary
    },
    dashboard: {
        name: 'dashboard',
        icon: 'lucide:house',
        color: appColors.slate
    },
    events: {
        name: 'events',
        icon: 'lucide:tent-tree',
        color: appColors.primary,
        search: {
            detail: 'events-id-eventBaseData',
            ability: ['view', 'events']
        },
    },
    participants: {
        name: 'participants',
        icon: 'lucide:backpack',
        color: appColors.primary,
        search: {
            path: 'spa.events.participants.index',
            params: [
                {
                    param: 'event',
                    prop: 'event_id'
                }
            ],
            listing: false,
            nameProp: 'fullname',
            detailProp: 'event_name',
            detail: 'events-event-participants-id-participantBaseData',
        },
    },
    participantGroups: {
        name: 'participantGroups',
        icon: 'lucide:tags', // echoes amosWEB's tag icon for Teilnehmergruppen
        color: appColors.primary,
    },
    guardians: {
        name: 'guardians',
        icon: 'lucide:shield-user',
        color: appColors.primary
    },
    submissions: {
        name: 'submissions',
        icon: 'lucide:file-pen-line',
        color: appColors.primary
    },
    leaders: {
        name: 'leaders',
        icon: 'lucide:clipboard-list',
        color: appColors.primary
    },
    crisisManagers: {
        name: 'crisisManagers',
        icon: 'lucide:phone-call',
        color: appColors.primary
    },
    emergencyContacts: {
        name: 'emergencyContacts',
        icon: 'lucide:phone-call',
        color: appColors.primary
    },
    temporaryAccesses: {
        name: 'temporaryAccesses',
        icon: 'lucide:qr-code',
        color: appColors.primary
    },
    questions: {
        name: 'questions',
        icon: 'lucide:list-ordered',
        color: appColors.teal
    },
    sets: {
        name: 'sets',
        icon: 'lucide:book-marked',
        color: appColors.teal,
        search: {
            detail: 'questions-sets-id-setBaseData',
            listing: 'questions-sets',
            ability: ['view', 'sets']
        },
    },
    pool: {
        name: 'pool',
        icon: 'lucide:globe-2',
        color: appColors.red
    },
    users: {
        name: 'users',
        icon: 'lucide:users-round',
        color: appColors.slate,
        search: {
            detail: 'users-id-userBaseData',
            nameProp: 'fullname',
            ability: ['viewAny', 'users']
        },
    },
    organisations: {
        name: 'organisations',
        icon: 'lucide:landmark',
        color: appColors.red,
        search: {
            detail: 'system-organisations-id-organisationBaseData',
            ability: ['viewAny', 'organisations']
        },
    },
    myOrganisation: {
        name: 'myOrganisation',
        icon: 'lucide:landmark',
        color: {...appColors.slate, DEFAULT: appColors.slate['900']},
    },
    settings: {
        name: 'settings',
        color: {...appColors.slate, DEFAULT: appColors.slate['900']},
        icon: 'lucide:settings',
    },
    system: {
        name: 'system',
        color: appColors.red,
        icon: 'lucide:cpu',
    },
    roles: {
        name: 'roles',
        color: appColors.red,
        icon: 'lucide:key-round',
    },
    addons: {
        name: 'addons',
        color: appColors.red,
        icon: 'lucide:package-plus',
    },
    billings: {
        name: 'billings',
        color: appColors.red,
        icon: 'lucide:receipt-text',
    },
    billingRuns: {
        name: 'billingRuns',
        color: appColors.red,
        icon: 'lucide:calendar-check',
    },
    pass: {
        name: 'pass',
        color: appColors.primary,
        icon: 'lucide:book-heart',
    },
    history: {
        name: 'history',
        color: {...appColors.slate, DEFAULT: appColors.slate['900']},
        icon: 'lucide:history',
    },
    mails: {
        name: 'mails',
        color: appColors.yellow,
        icon: 'lucide:mailbox',
        search: {
            nameProp: 'subject',
            detailProp: 'email',
            detail: 'mails-id-mailBaseData',
        },
    },
    archive: {
        name: 'archive',
        color: {...appColors.stone, DEFAULT: appColors.stone['400']},
        icon: 'lucide:archive',
    },
    emergency: {
        name: 'emergency',
        color: appColors.red,
        icon: 'emergency',
    }
}
